import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { DashboardProvider } from "@/contexts/DashboardContext";
import Index from "./pages/auth/Index";
import Dashboard from "./pages/dashboard/Dashboard";
import ProjectList from "./pages/projects/ProjectList";
import ProjectDetail from "./pages/projects/ProjectDetail";
import AddProject from "./pages/projects/AddProject";
import OrganizationList from "./pages/organizations/OrganizationList";
import OrganizationDetail from "./pages/organizations/OrganizationDetail";
import AddOrganization from "./pages/organizations/AddOrganization";
import UsersRoles from "./pages/users/UsersRoles";
import Tasks from "./pages/tasks/Tasks";
import Reports from "./pages/reports/Reports";
import TechnicalDocumentation from "./pages/reports/TechnicalDocumentation";
import AuthCallback from "./pages/auth/AuthCallback";
import Settings from "./pages/settings/Settings";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when network reconnects
      staleTime: 0, // Always consider data stale so it refetches on every navigation
      retry: 1, // Retry failed requests once
    },
  },
});

// Conditionally protect routes only in development
const ConditionalProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  return !import.meta.env.DEV ? <ProtectedRoute>{children}</ProtectedRoute> : <>{children}</>;
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
            <LanguageProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/oauth/callback" element={<AuthCallback />} />             
                <Route path="/dashboard" element={<ConditionalProtectedRoute><DashboardProvider><Dashboard /></DashboardProvider></ConditionalProtectedRoute>} />
                <Route path="/organization" element={<ConditionalProtectedRoute><OrganizationList /></ConditionalProtectedRoute>} />
                <Route path="/organization/:id" element={<ConditionalProtectedRoute><OrganizationDetail /></ConditionalProtectedRoute>} />
                <Route path="/organization/add" element={<ConditionalProtectedRoute><AddOrganization /></ConditionalProtectedRoute>} />
                <Route path="/project" element={<ConditionalProtectedRoute><ProjectList /></ConditionalProtectedRoute>} />
                <Route path="/project/:id" element={<ConditionalProtectedRoute><ProjectDetail /></ConditionalProtectedRoute>} />
                <Route path="/project/add" element={<ConditionalProtectedRoute><AddProject /></ConditionalProtectedRoute>} />
                <Route path="/users" element={<ConditionalProtectedRoute><UsersRoles /></ConditionalProtectedRoute>} />
                <Route path="/tasks" element={<ConditionalProtectedRoute><Tasks /></ConditionalProtectedRoute>} />
                <Route path="/reports" element={<ConditionalProtectedRoute><Reports /></ConditionalProtectedRoute>} />
                <Route path="/technical-docs" element={<ConditionalProtectedRoute><TechnicalDocumentation /></ConditionalProtectedRoute>} />
                <Route path="/settings" element={<ConditionalProtectedRoute><Settings /></ConditionalProtectedRoute>} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </TooltipProvider>
            </LanguageProvider>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  </QueryClientProvider>
);

export default App;
